import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabaseClient';
import Swal from 'sweetalert2';

interface GlobalSettings {
  id: string; app_name: string; support_email: string;
  allow_registration: boolean; maintenance_mode: boolean;
}

const EMPTY: GlobalSettings = {
  id: '', app_name: 'Pixory AI', support_email: 'support@pixory.ai',
  allow_registration: true, maintenance_mode: false,
};

const SW = { background: '#ffffff', color: '#0f172a' };

export default function GlobalSettingsTab() {
  const [settings, setSettings] = useState<GlobalSettings>(EMPTY);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [dirty, setDirty]       = useState(false);

  useEffect(() => {
    supabase.from('saas_global_settings').select('*').maybeSingle().then(({ data }) => {
      if (data) setSettings(data as GlobalSettings);
      setLoading(false);
    });
  }, []);

  const set = <K extends keyof GlobalSettings>(key: K, value: GlobalSettings[K]) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    const payload = { app_name: settings.app_name, support_email: settings.support_email, allow_registration: settings.allow_registration, maintenance_mode: settings.maintenance_mode, updated_at: new Date().toISOString() };
    const { error } = settings.id
      ? await supabase.from('saas_global_settings').update(payload).eq('id', settings.id)
      : await supabase.from('saas_global_settings').insert({ app_name: settings.app_name, support_email: settings.support_email, allow_registration: settings.allow_registration, maintenance_mode: settings.maintenance_mode });
    setSaving(false);
    if (error) { Swal.fire({ icon: 'error', title: 'Error', text: error.message, ...SW }); return; }
    setDirty(false);
    Swal.fire({ icon: 'success', title: 'Configuración guardada', timer: 1400, showConfirmButton: false, ...SW });
  };

  const Toggle = ({ label, desc, value, onChange }: { label: string; desc: string; value: boolean; onChange: (v: boolean) => void }) => (
    <div className="flex items-center justify-between gap-4 py-4 border-b border-slate-100 last:border-0">
      <div>
        <p className="text-slate-900 text-sm font-medium">{label}</p>
        <p className="text-slate-500 text-xs mt-0.5">{desc}</p>
      </div>
      <button type="button" onClick={() => onChange(!value)}
        className={`relative w-11 h-6 rounded-full transition-colors shrink-0 cursor-pointer ${value ? 'bg-[#2563eb]' : 'bg-slate-200'}`}>
        <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all shadow-sm ${value ? 'left-5' : 'left-0.5'}`} />
      </button>
    </div>
  );

  if (loading) return (
    <div className="flex items-center justify-center py-16">
      <div className="w-5 h-5 border-2 border-blue-200 border-t-[#2563eb] rounded-full animate-spin"></div>
    </div>
  );

  return (
    <div className="space-y-6 max-w-2xl">
      <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
        <h4 className="text-slate-500 text-xs uppercase tracking-wider font-medium">Identificación</h4>
        <div>
          <label className="block text-slate-500 text-xs mb-1.5">Nombre de la plataforma</label>
          <input value={settings.app_name} onChange={(e) => set('app_name', e.target.value)}
            className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-slate-900 text-sm placeholder-slate-400 focus:outline-none focus:border-[#2563eb] transition-colors" />
          <p className="text-slate-400 text-[11px] mt-1">Aparece en el login, emails y navegador.</p>
        </div>
        <div>
          <label className="block text-slate-500 text-xs mb-1.5">Email de soporte</label>
          <input type="email" value={settings.support_email} onChange={(e) => set('support_email', e.target.value)}
            className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-slate-900 text-sm placeholder-slate-400 focus:outline-none focus:border-[#2563eb] transition-colors" />
          <p className="text-slate-400 text-[11px] mt-1">Usado en emails automáticos como dirección de respuesta.</p>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl px-5">
        <h4 className="text-slate-500 text-xs uppercase tracking-wider font-medium py-4 border-b border-slate-100">Control de acceso</h4>
        <Toggle label="Permitir registro" desc="Si está desactivado, nuevos tenants no podrán registrarse en /register." value={settings.allow_registration} onChange={(v) => set('allow_registration', v)} />
        <Toggle label="Modo mantenimiento" desc="Muestra aviso de mantenimiento a tenants. Super Admin mantiene acceso." value={settings.maintenance_mode} onChange={(v) => set('maintenance_mode', v)} />
      </div>

      {settings.maintenance_mode && (
        <div className="flex items-start gap-3 px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl">
          <span className="w-5 h-5 flex items-center justify-center text-amber-600 mt-0.5 shrink-0"><i className="ri-error-warning-line text-sm"></i></span>
          <p className="text-amber-700 text-sm"><strong>Mantenimiento activo</strong> — los tenants no podrán acceder a la plataforma hasta que desactives esta opción.</p>
        </div>
      )}

      <div className="flex items-center justify-end gap-3">
        {dirty && <p className="text-slate-400 text-xs">Cambios sin guardar</p>}
        <button onClick={handleSave} disabled={saving || !dirty}
          className="flex items-center gap-2 px-5 py-2.5 bg-[#2563eb] hover:bg-blue-700 disabled:opacity-40 text-white text-sm font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap">
          <span className="w-4 h-4 flex items-center justify-center"><i className={saving ? 'ri-loader-4-line animate-spin' : 'ri-save-line'}></i></span>
          {saving ? 'Guardando…' : 'Guardar configuración'}
        </button>
      </div>
    </div>
  );
}
